import { useState, useEffect } from "react";
import Swipeable from "@/components/Swipeable";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  Shield, 
  CheckCircle2, 
  Phone, 
  MessageCircle,
  Building2,
  Award,
  Clock,
  Heart,
  User,
  Calendar,
  IndianRupee,
  AlertTriangle,
  Loader2,
  Ambulance,
  Stethoscope,
  Percent,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  Sparkles,
  ArrowLeft,
  Home,
  ShieldCheck
} from "lucide-react";
import { Button } from "../components/ui/button";
import ClickableFeature from "../components/ClickableFeature";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// IRDAI Insurance Statistics Handbook 2024-25 Data (Same as Compare Plans)
const CLAIM_EXPERIENCE_DATA = {
  "hdfc-ergo": {
    claim_settlement_ratio: "98.33%",
    incurred_claim_ratio: "87.04%",
    icr_previous_year: "84.85%",
    icr_trend: "Increased",
    solvency_ratio: "2.00",
    complaints_per_10k: "2.3",
    gross_premium_cr: "₹5,720 Cr",
    network_hospitals: "13,500+",
    cashless_hospitals: "10,500+",
    irdai_health_category: "Private General Insurer",
  },
  "care-health": {
    claim_settlement_ratio: "96.85%",
    incurred_claim_ratio: "65.10%",
    icr_previous_year: "64.53%",
    icr_trend: "Stable",
    solvency_ratio: "1.68",
    complaints_per_10k: "7.2",
    gross_premium_cr: "₹8,018 Cr",
    network_hospitals: "25,000+",
    cashless_hospitals: "21,500+",
    irdai_health_category: "Standalone Health Insurer",
  },
  "icici-lombard": {
    claim_settlement_ratio: "90.72%",
    incurred_claim_ratio: "85.53%",
    icr_previous_year: "82.24%",
    icr_trend: "Increased",
    solvency_ratio: "2.69",
    complaints_per_10k: "2.8",
    gross_premium_cr: "₹6,950 Cr",
    network_hospitals: "11,000+",
    cashless_hospitals: "9,500+",
    irdai_health_category: "Private General Insurer",
  },
  "aditya-birla": {
    claim_settlement_ratio: "95.94%",
    incurred_claim_ratio: "75.97%",
    icr_previous_year: "71.50%",
    icr_trend: "Increased",
    solvency_ratio: "1.84",
    complaints_per_10k: "2.3",
    gross_premium_cr: "₹4,397 Cr",
    network_hospitals: "10,800+",
    cashless_hospitals: "10,500+",
    irdai_health_category: "Standalone Health Insurer",
  },
  "manipal-cigna": {
    claim_settlement_ratio: "93.71%",
    incurred_claim_ratio: "73.31%",
    icr_previous_year: "64%",
    icr_trend: "Increased",
    solvency_ratio: "1.76",
    complaints_per_10k: "4.5",
    gross_premium_cr: "₹1,765 Cr",
    network_hospitals: "8,200+",
    cashless_hospitals: "7,800+",
    irdai_health_category: "Standalone Health Insurer",
  },
  "niva-bupa": {
    claim_settlement_ratio: "92.55%",
    incurred_claim_ratio: "61.63%",
    icr_previous_year: "59.02%",
    icr_trend: "Increased",
    solvency_ratio: "3.03",
    complaints_per_10k: "8.5",
    gross_premium_cr: "₹6,616 Cr",
    network_hospitals: "10,500+",
    cashless_hospitals: "10,000+",
    irdai_health_category: "Standalone Health Insurer",
  },
  "star-health": {
    claim_settlement_ratio: "86.42%",
    incurred_claim_ratio: "70.53%",
    icr_previous_year: "68.2%",
    icr_trend: "Increased",
    solvency_ratio: "2.21",
    complaints_per_10k: "10.2",
    gross_premium_cr: "₹16,517 Cr",
    network_hospitals: "14,500+",
    cashless_hospitals: "14,000+",
    irdai_health_category: "Standalone Health Insurer",
  },
  "bajaj-allianz": {
    claim_settlement_ratio: "98.08%",
    incurred_claim_ratio: "90.01%",
    icr_previous_year: "84.96%",
    icr_trend: "Increased",
    solvency_ratio: "3.25",
    complaints_per_10k: "0.6",
    gross_premium_cr: "₹7,637 Cr",
    network_hospitals: "18,800+",
    cashless_hospitals: "18,500+",
    irdai_health_category: "Private General Insurer",
  },
  "tata-aig": {
    claim_settlement_ratio: "96.63%",
    incurred_claim_ratio: "80.65%",
    icr_previous_year: "72.5%",
    icr_trend: "Increased",
    solvency_ratio: "1.81",
    complaints_per_10k: "1.8",
    gross_premium_cr: "₹2,921 Cr",
    network_hospitals: "10,200+",
    cashless_hospitals: "9,800+",
    irdai_health_category: "Private General Insurer",
  },
  "digit": {
    claim_settlement_ratio: "96.83%",
    incurred_claim_ratio: "90.06%",
    icr_previous_year: "78%",
    icr_trend: "Increased",
    solvency_ratio: "2.24",
    complaints_per_10k: "2.6",
    gross_premium_cr: "₹1,178 Cr",
    network_hospitals: "11,500+",
    cashless_hospitals: "11,200+",
    irdai_health_category: "Private General Insurer",
  },
  "new-india": {
    claim_settlement_ratio: "98.44%",
    incurred_claim_ratio: "101.28%",
    icr_previous_year: "103%",
    icr_trend: "Improved",
    solvency_ratio: "1.91",
    complaints_per_10k: "0.6",
    gross_premium_cr: "₹19,188 Cr",
    network_hospitals: "8,800+",
    cashless_hospitals: "8,500+",
    irdai_health_category: "PSU General Insurer",
  }
};

// Map company name to company ID (same as Compare Plans)
const getCompanyId = (company) => {
  if (!company) return null;
  const c = company.toLowerCase();
  if (c.includes("hdfc")) return "hdfc-ergo";
  if (c.includes("care")) return "care-health";
  if (c.includes("icici")) return "icici-lombard";
  if (c.includes("aditya") || c.includes("birla")) return "aditya-birla";
  if (c.includes("manipal") || c.includes("cigna")) return "manipal-cigna";
  if (c.includes("niva") || c.includes("bupa")) return "niva-bupa";
  if (c.includes("star")) return "star-health";
  if (c.includes("bajaj")) return "bajaj-allianz";
  if (c.includes("tata") && !c.includes("aia")) return "tata-aig";
  if (c.includes("digit")) return "digit";
  if (c.includes("new india")) return "new-india";
  return null;
};

// Extract comparison data from full plan details (SAME LOGIC AS COMPARE PLANS)
const extractComparisonData = (plan) => {
  if (!plan) return {};
  
  const getRoomRent = () => {
    const baseRoomRent = plan.coverage_details?.hospitalization?.room_rent || "Covered up to SI";
    if (plan.id === 'icici-elevate') {
      return `${baseRoomRent} (Any Room Category available as modifier)`;
    }
    const roomModifier = plan.riders?.find(r => 
      r.name.toLowerCase().includes('room') || r.description?.toLowerCase().includes('room')
    );
    if (roomModifier) return `${baseRoomRent} (${roomModifier.name} available)`;
    return baseRoomRent;
  };
  
  const getAirAmbulance = () => {
    const baseAir = plan.coverage_details?.ambulance?.air || "Not Covered";
    const airRider = plan.riders?.find(r => r.name.toLowerCase().includes('air ambulance'));
    if (airRider) return `${baseAir} (via ${airRider.name})`;
    return baseAir;
  };
  
  const getRoadAmbulance = () => plan.coverage_details?.ambulance?.road || "Covered";
  
  const getMaternity = () => {
    const maternityRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('maternity') || r.name.toLowerCase().includes('parenthood')
    );
    if (maternityRider) {
      const waitingPeriod = maternityRider.waiting_period || "24 months";
      return `Optional: ${maternityRider.name} (WP: ${waitingPeriod})`;
    }
    return "Not Covered";
  };
  
  const getOPD = () => {
    const opdRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('opd') || r.name.toLowerCase().includes('wellbeing') || r.name.toLowerCase().includes('befit')
    );
    if (opdRider) return `Optional: ${opdRider.name}`;
    return "Not Covered";
  };
  
  const getConsumables = () => {
    const consumableRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('consumable') || r.name.toLowerCase().includes('claim shield') || r.name.toLowerCase().includes('claim protector')
    );
    if (consumableRider) return `Covered via ${consumableRider.name}`;
    if (plan.coverage_details?.consumables) return plan.coverage_details.consumables;
    return "Covered up to SI";
  };
  
  const getRestoreBenefit = () => {
    const restoreBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('restore') || b.name.toLowerCase().includes('recharge') ||
      b.name.toLowerCase().includes('reset') || b.name.toLowerCase().includes('secure')
    );
    if (restoreBenefit) {
      const detail = restoreBenefit.details?.[0] || restoreBenefit.description;
      return detail || "Available";
    }
    const restoreRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('restore') || r.name.toLowerCase().includes('recharge') || r.name.toLowerCase().includes('unlimited care')
    );
    if (restoreRider) return `Optional: ${restoreRider.name}`;
    return "Available";
  };
  
  const getNCB = () => {
    const ncbBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('bonus') || b.name.toLowerCase().includes('cumulative') || b.name.toLowerCase().includes('loyalty')
    );
    if (ncbBenefit) {
      const detail = ncbBenefit.details?.[0] || ncbBenefit.description;
      return detail || "50% per year up to 100%";
    }
    return "50% per year up to 100%";
  };
  
  const getNCBReduction = () => {
    const ncbBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('bonus') || b.name.toLowerCase().includes('cumulative')
    );
    if (ncbBenefit?.details) {
      const reduction = ncbBenefit.details.find(d => 
        d.toLowerCase().includes('reduce') || d.toLowerCase().includes('claim') || d.toLowerCase().includes('maintain')
      );
      return reduction || "May reduce on claim";
    }
    return "May reduce on claim";
  };
  
  const getPEDModification = () => {
    const pedInfo = plan.waiting_periods?.pre_existing?.reduction;
    const pedRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('ped') || r.name.toLowerCase().includes('jumpstart') || r.name.toLowerCase().includes('pre-existing')
    );
    if (pedRider) return `${pedInfo || 'Standard'} (${pedRider.name} available)`;
    return pedInfo || "Standard (36 months)";
  };
  
  const getHealthCheckup = () => {
    if (plan.health_checkup) {
      const limits = plan.health_checkup.limits_individual || plan.health_checkup.limits_floater;
      if (limits) {
        const firstKey = Object.keys(limits)[0];
        const firstLimit = limits[firstKey];
        return `${firstLimit}`;
      }
      if (plan.health_checkup.availability) return plan.health_checkup.availability;
    }
    return "Covered as per plan";
  };
  
  const getHospitalCash = () => {
    const hcRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('hospital cash') || r.name.toLowerCase().includes('daily cash')
    );
    if (hcRider) return `Optional: ${hcRider.name}`;
    return "Not Available";
  };
  
  const getCriticalIllness = () => {
    const ciRider = plan.riders?.find(r => r.name.toLowerCase().includes('critical illness'));
    if (ciRider) {
      const limit = ciRider.details?.find(d => d.includes('₹') || d.includes('Lakh'));
      return `Optional: ${ciRider.name}${limit ? ` (${limit})` : ''}`;
    }
    return "Not Covered";
  };
  
  const getAYUSHPrePost = () => {
    const ayushInfo = plan.coverage_details?.ayush;
    if (ayushInfo?.pre_post) return ayushInfo.pre_post;
    if (ayushInfo?.type?.toLowerCase().includes('in-patient') || ayushInfo?.type?.toLowerCase().includes('inpatient')) {
      return "Inpatient Only";
    }
    return "Inpatient Only";
  };
  
  const getMultiYearDiscount = () => {
    if (!plan.discounts) return "7.5% (2yr) / 10% (3yr)";
    const discount = plan.discounts.find(d => {
      const name = typeof d === 'string' ? d : d?.name;
      return name?.toLowerCase().includes('multi') || name?.toLowerCase().includes('long') || name?.toLowerCase().includes('tenure');
    });
    if (discount) {
      if (typeof discount === 'string') return discount;
      return discount.description || discount.discount || "7.5% (2yr) / 10% (3yr)";
    }
    return "7.5% (2yr) / 10% (3yr)";
  };

  const findDiscount = (keyword) => {
    if (!plan.discounts) return "Available";
    const discount = plan.discounts.find(d => {
      const name = typeof d === 'string' ? d : d?.name;
      return name?.toLowerCase().includes(keyword);
    });
    if (discount) {
      if (typeof discount === 'string') return discount;
      return discount.discount || discount.description || "Available";
    }
    return "Available";
  };
  
  const data = {
    sum_insured_display: plan.sum_insured_options?.slice(0, 5).join(", ") + (plan.sum_insured_options?.length > 5 ? "..." : "") || "N/A",
    room_rent: getRoomRent(),
    icu_limit: plan.coverage_details?.hospitalization?.icu_charges || plan.coverage_details?.hospitalization?.icu || "Covered up to SI",
    reasonable_customary_charges: plan.reasonable_customary_charges || "Applicable - Charges capped at prevailing rates",
    policy_tenure: plan.policy_options?.tenure?.join(", ") || "1, 2, 3 Years",
    family_floater: plan.policy_options?.max_members_floater || "Available",
    pre_hospitalization: plan.coverage_details?.pre_hospitalization?.days || "60 Days",
    post_hospitalization: plan.coverage_details?.post_hospitalization?.days || "180 Days",
    day_care: plan.coverage_details?.day_care?.procedures || "All Covered",
    domiciliary: plan.coverage_details?.domiciliary?.coverage || "Covered up to SI",
    organ_donor: plan.coverage_details?.organ_donor?.coverage || "Covered up to SI",
    road_ambulance: getRoadAmbulance(),
    air_ambulance: getAirAmbulance(),
    restore_benefit: getRestoreBenefit(),
    ncb: getNCB(),
    ncb_reduction: getNCBReduction(),
    ayush_coverage: plan.coverage_details?.ayush?.coverage || "Covered up to SI",
    ayush_pre_post: getAYUSHPrePost(),
    initial_waiting: plan.waiting_periods?.initial?.period || "30 Days",
    specific_disease_waiting: plan.waiting_periods?.specific_diseases?.period || "24 Months",
    pre_existing_waiting: plan.waiting_periods?.pre_existing?.period || "36 Months",
    ped_modification: getPEDModification(),
    health_checkup: getHealthCheckup(),
    consumables: getConsumables(),
    robotics: plan.coverage_details?.advanced_technology?.coverage || "Covered up to SI",
    second_opinion: "Available",
    maternity: getMaternity(),
    opd: getOPD(),
    hospital_cash: getHospitalCash(),
    critical_illness: getCriticalIllness(),
    copay_age: "No Co-pay",
    copay_zone: "No Zonal Co-pay",
    copay_room: "Proportionate deduction if room limit exceeded",
    sub_limits: getRoomRent().includes("No sub-limit") || getRoomRent().includes("No Capping") || getRoomRent().includes("At Actuals") ? "No Sub-limits" : "May have sub-limits",
    multi_year_discount: getMultiYearDiscount(),
    family_discount: findDiscount('family'),
    online_discount: findDiscount('online'),
  };
  
  // Add IRDAI claim experience data
  const companyId = getCompanyId(plan.company);
  if (companyId && CLAIM_EXPERIENCE_DATA[companyId]) {
    const claimData = CLAIM_EXPERIENCE_DATA[companyId];
    data.claim_settlement_ratio = claimData.claim_settlement_ratio;
    data.incurred_claim_ratio = claimData.incurred_claim_ratio;
    data.icr_previous_year = claimData.icr_previous_year;
    data.icr_trend = claimData.icr_trend;
    data.solvency_ratio = claimData.solvency_ratio;
    data.complaints_per_10k = claimData.complaints_per_10k;
    data.gross_premium_cr = claimData.gross_premium_cr;
    data.network_hospitals = claimData.network_hospitals;
    data.cashless_hospitals = claimData.cashless_hospitals;
    data.irdai_health_category = claimData.irdai_health_category;
  }
  
  return data;
};

// Comparison categories (SAME AS COMPARE PLANS)
const COMPARISON_CATEGORIES = [
  {
    id: "claim_experience",
    name: "Claim Experience (IRDAI FY 2024-25)",
    icon: Award,
    color: "from-[#EDF1F8] to-cyan-500",
    fields: [
      { key: "claim_settlement_ratio", label: "Claim Settlement Ratio (CSR)" },
      { key: "incurred_claim_ratio", label: "Incurred Claim Ratio (ICR)" },
      { key: "solvency_ratio", label: "Solvency Ratio" },
      { key: "complaints_per_10k", label: "Complaints per 10K Claims" },
      { key: "network_hospitals", label: "Network Hospitals" },
      { key: "cashless_hospitals", label: "Cashless Hospitals" },
      { key: "irdai_health_category", label: "IRDAI Category" },
    ]
  },
  {
    id: "basic_policy",
    name: "Basic Policy Details",
    icon: Shield,
    color: "from-indigo-500 to-purple-500",
    fields: [
      { key: "sum_insured_display", label: "Sum Insured Options" },
      { key: "room_rent", label: "Room Rent" },
      { key: "icu_limit", label: "ICU Limit" },
      { key: "reasonable_customary_charges", label: "Reasonable & Customary Charges" },
      { key: "policy_tenure", label: "Policy Tenure" },
      { key: "family_floater", label: "Family Floater" },
    ]
  },
  {
    id: "room_hospitalization",
    name: "Room & Hospitalization Coverage",
    icon: Building2,
    color: "from-purple-500 to-pink-500",
    fields: [
      { key: "pre_hospitalization", label: "Pre-Hospitalization" },
      { key: "post_hospitalization", label: "Post-Hospitalization" },
      { key: "day_care", label: "Day Care Procedures" },
      { key: "domiciliary", label: "Domiciliary Treatment" },
      { key: "organ_donor", label: "Organ Donor Coverage" },
    ]
  },
  {
    id: "ambulance",
    name: "Ambulance & Emergency",
    icon: Ambulance,
    color: "from-red-500 to-rose-500",
    fields: [
      { key: "road_ambulance", label: "Road Ambulance" },
      { key: "air_ambulance", label: "Air Ambulance" },
    ]
  },
  {
    id: "restore_bonus",
    name: "Restore & Bonus Benefits",
    icon: Percent,
    color: "from-[#D4A017] to-yellow-500",
    fields: [
      { key: "restore_benefit", label: "Restore/Recharge Benefit" },
      { key: "ncb", label: "No Claim Bonus (NCB)" },
      { key: "ncb_reduction", label: "NCB Reduction on Claim" },
    ]
  },
  {
    id: "ayush",
    name: "AYUSH & Alternative Medicine",
    icon: Heart,
    color: "from-green-500 to-emerald-500",
    fields: [
      { key: "ayush_coverage", label: "AYUSH Treatment Coverage" },
      { key: "ayush_pre_post", label: "AYUSH Pre/Post Coverage" },
    ]
  },
  {
    id: "waiting_periods",
    name: "Waiting Periods",
    icon: Clock,
    color: "from-[#D4A017] to-[#D9A82C]",
    fields: [
      { key: "initial_waiting", label: "Initial Waiting Period" },
      { key: "pre_existing_waiting", label: "Pre-Existing Disease (PED)" },
      { key: "specific_disease_waiting", label: "Specific Disease Waiting" },
      { key: "ped_modification", label: "PED Reduction Option" },
    ]
  },
  {
    id: "additional",
    name: "Additional Benefits",
    icon: Stethoscope,
    color: "from-cyan-500 to-[#DCE4F2]",
    fields: [
      { key: "consumables", label: "Consumables Coverage" },
      { key: "health_checkup", label: "Annual Health Checkup" },
      { key: "robotics", label: "Robotic Surgery" },
      { key: "second_opinion", label: "Second E-Opinion" },
    ]
  },
  {
    id: "optional",
    name: "Optional Coverages (Riders)",
    icon: Plus,
    color: "from-pink-500 to-rose-500",
    fields: [
      { key: "maternity", label: "Maternity Coverage" },
      { key: "opd", label: "OPD Coverage" },
      { key: "hospital_cash", label: "Hospital Daily Cash" },
      { key: "critical_illness", label: "Critical Illness Cover" },
    ]
  },
  {
    id: "copay_limits",
    name: "Co-payments & Limits",
    icon: AlertTriangle,
    color: "from-yellow-500 to-[#D9A82C]",
    fields: [
      { key: "copay_age", label: "Age-Based Co-payment" },
      { key: "copay_zone", label: "Zonal Co-payment" },
      { key: "copay_room", label: "Room Upgrade Co-payment" },
      { key: "sub_limits", label: "Sub-Limits" },
    ]
  },
];

const PREMIUM_TERMS = [
  { id: "annual", label: "1 Year" },
  { id: "2year", label: "2 Years" },
  { id: "3year", label: "3 Years" },
];

export default function QuoteView() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(["claim_experience", "basic_policy"]);
  const [showRiders, setShowRiders] = useState(true);

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/quotes/public/${quoteId}`);
      if (!response.ok) {
        throw new Error("Quote not found or expired");
      }
      const data = await response.json();
      setQuote(data);
      
      // Fetch full plan details for each plan in the quote
      if (data.selected_plans && data.selected_plans.length > 0) {
        const planDetailsPromises = data.selected_plans.map(async (quotePlan) => {
          try {
            const planResponse = await fetch(`${API}/plans/${quotePlan.plan_id}`);
            if (planResponse.ok) {
              const planData = await planResponse.json();
              return {
                ...planData,
                quotePlan: quotePlan
              };
            }
          } catch (err) {
            console.error(`Error fetching plan ${quotePlan.plan_id}:`, err);
          }
          return null;
        });
        
        const planDetails = await Promise.all(planDetailsPromises);
        setPlans(planDetails.filter(p => p !== null));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Render value with clickable feature (SAME AS COMPARE PLANS)
  const renderValue = (value, field, plan = null) => {
    if (!value || value === "N/A") {
      return <span className="text-[#3A4557]">N/A</span>;
    }
    
    const valueStr = String(value).toLowerCase();
    const isPositive = valueStr.includes('covered') || valueStr.includes('available') || 
                       valueStr.includes('unlimited') || valueStr.includes('no co-pay') ||
                       valueStr.includes('no sub-limit') || valueStr.includes('no capping');
    const isNegative = valueStr.includes('not covered') || valueStr.includes('not available') || valueStr.includes('excluded');
    
    // Wrap content in ClickableFeature if plan info is available
    const wrapWithClickable = (content) => {
      if (plan && field?.key) {
        return (
          <ClickableFeature
            featureKey={field.key}
            value={value}
            planId={plan.id}
            planName={plan.name}
            planDocuments={plan.documents}
            showIcon={false}
          >
            {content}
          </ClickableFeature>
        );
      }
      return content;
    };
    
    if (isPositive) {
      return (
        <span className="flex items-center justify-center gap-1 text-emerald-600">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          {wrapWithClickable(<span className="text-sm">{value}</span>)}
        </span>
      );
    }
    
    if (isNegative) {
      return (
        <span className="flex items-center justify-center gap-1 text-red-600">
          <X className="h-4 w-4 flex-shrink-0" />
          {wrapWithClickable(<span className="text-sm">{value}</span>)}
        </span>
      );
    }
    
    return wrapWithClickable(<span className="text-[#2E3B57] text-sm">{value}</span>);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#3A4557]">Loading your personalized comparison...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card p-8 text-center max-w-md w-full">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#1B2A4A] mb-2">Quote Not Found</h2>
          <p className="text-[#3A4557] mb-6">{error}</p>
          <Link to="/">
            <Button className="btn-gold">Go to Homepage</Button>
          </Link>
        </div>
      </div>
    );
  }

  const selectedPlans = quote?.selected_plans || [];
  const numPlans = plans.length;
  
  // Extract comparison data from full plan details
  const comparisonData = plans.map(plan => ({
    plan,
    data: extractComparisonData(plan)
  }));
  
  // Get all unique riders across all plans
  const allRiders = new Map();
  plans.forEach(plan => {
    plan.riders?.forEach(rider => {
      if (!allRiders.has(rider.name)) {
        allRiders.set(rider.name, rider.description || '');
      }
    });
  });

  return (
    <div className="min-h-screen pb-12" data-testid="quote-view-page">
      {/* Header - Same style as Compare Plans */}
      <header className="glass-nav sticky top-2 mx-2 sm:mx-6 z-50 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between rounded-xl sm:rounded-2xl">
        <div className="flex items-center gap-2 sm:gap-4">
          <Link to="/">
            <Button 
              variant="ghost" 
              className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3"
              title="Home"
            >
              <Home className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-[#1B2A4A]" />
            </div>
            <div>
              <h1 className="font-bold text-[#1B2A4A] text-sm sm:text-base">Kavach Box</h1>
              <p className="text-xs text-[#3A4557] hidden sm:block">by Prashant K Uunadkat</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="tel:+919510020907">
            <Button className="btn-gold h-8 sm:h-10 px-3 sm:px-5 text-xs sm:text-sm">
              <Phone className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Call Now</span>
            </Button>
          </a>
          <a href="https://wa.me/919979860107" target="_blank" rel="noopener noreferrer">
            <Button className="bg-emerald-600 hover:bg-emerald-700 h-8 sm:h-10 px-3 sm:px-5 text-xs sm:text-sm">
              <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">WhatsApp</span>
            </Button>
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-2 sm:px-6 py-4 sm:py-8">
        {/* Quote Info Card */}
        <div className="glass-card p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-xl font-bold text-[#1B2A4A] mb-1">Health Insurance Comparison Quote</h2>
              <p className="text-[#3A4557] text-sm">Detailed plan comparison prepared for you</p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#EDF1F8]/40 border border-blue-500/30 rounded-lg">
                <User className="h-4 w-4 text-[#2E3B57]" />
                <span className="text-[#1B2A4A] font-medium">{quote?.client_name}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#D4A017]/15 border border-[#D4A017]/30 rounded-lg">
                <Calendar className="h-4 w-4 text-[#A87608]" />
                <span className="text-[#2E3B57]">{new Date(quote?.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-[#EDF1F8]/40 text-[#2E3B57] rounded-full text-sm font-medium">
              {numPlans} Plans Compared
            </span>
            <span className="px-3 py-1 bg-emerald-500/20 text-emerald-600 rounded-full text-sm font-medium">
              IRDAI Data FY 2024-25
            </span>
          </div>
        </div>

        {/* Desktop Plan Headers - Sticky (Same as Compare Plans) */}
        <div className="sticky top-16 z-30 -mx-2 px-2 pt-1 pb-2" style={{ backgroundColor: 'rgba(10, 22, 40, 0.97)', backdropFilter: 'blur(8px)' }}>
          <div className="glass-card p-4 border-[#D4A017]/30"><Swipeable hint>
            <div 
              className="grid gap-4 items-center"
              style={{ 
                gridTemplateColumns: numPlans >= 4 
                  ? `160px repeat(${numPlans}, minmax(160px, 1fr))`
                  : `200px repeat(${numPlans}, minmax(200px, 1fr))`,
                minWidth: numPlans >= 4 ? `${160 + numPlans * 160}px` : 'auto'
              }}
            >
              <div className="flex items-center">
                <span className="font-bold text-[#1B2A4A] text-sm">Plan Details</span>
              </div>
              {plans.map((plan) => (
                <div key={plan.id} className="flex items-center justify-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white p-1.5 flex-shrink-0">
                    <img
                      src={plan.company_logo}
                      alt={plan.company}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div className="text-left min-w-0">
                    <h3 className="font-bold text-[#1B2A4A] text-sm truncate">{plan.name}</h3>
                    <p className="text-xs text-[#3A4557] truncate">{plan.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </Swipeable></div>
        </div>

        {/* Comparison Categories (Same as Compare Plans) */}
        {COMPARISON_CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isExpanded = expandedCategories.includes(category.id);

          return (
            <div
              key={category.id}
              className="glass-card mb-4 overflow-hidden"
            >
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-[#EDF1F8]/30 transition-colors"
              >
                <div className="flex items-center gap-2 md:gap-3">
                  <div className={`h-8 w-8 md:h-10 md:w-10 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                    <Icon className="h-4 w-4 md:h-5 md:w-5 text-[#1B2A4A]" />
                  </div>
                  <h2 className="font-bold text-[#1B2A4A] text-sm md:text-base">{category.name}</h2>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-[#3A4557]" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                )}
              </button>

              {isExpanded && (
                <div className="border-t border-[#C9D4E6]">
                  <Swipeable hint>
                    <div style={{ minWidth: numPlans >= 4 ? `${160 + numPlans * 160}px` : '600px' }}>
                      {category.fields.map((field, fieldIndex) => (
                        <div
                          key={field.key}
                          className={`grid gap-4 p-4 ${fieldIndex % 2 === 0 ? 'bg-blue-900/20' : ''}`}
                          style={{ 
                            gridTemplateColumns: numPlans >= 4
                              ? `160px repeat(${numPlans}, minmax(160px, 1fr))`
                              : `200px repeat(${numPlans}, minmax(200px, 1fr))`
                          }}
                        >
                          <div className="flex items-center">
                            <span className="text-[#2E3B57] text-sm font-medium">{field.label}</span>
                          </div>
                          {comparisonData.map(({ plan, data }) => (
                            <div key={plan.id} className="flex items-center justify-center text-center px-2">
                              {renderValue(data[field.key], field, plan)}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </Swipeable>
                </div>
              )}
            </div>
          );
        })}

        {/* Add-on Riders (Same as Compare Plans) */}
        <div className="glass-card overflow-hidden mb-4">
          <button
            onClick={() => setShowRiders(!showRiders)}
            className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-[#EDF1F8]/30 transition-colors"
          >
            <div className="flex items-center gap-2 md:gap-3">
              <div className="h-8 w-8 md:h-10 md:w-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                <Sparkles className="h-4 w-4 md:h-5 md:w-5 text-[#1B2A4A]" />
              </div>
              <h2 className="font-bold text-[#1B2A4A] text-sm md:text-base">Add-on Riders</h2>
            </div>
            {showRiders ? (
              <ChevronUp className="h-5 w-5 text-[#3A4557]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[#3A4557]" />
            )}
          </button>

          {showRiders && allRiders.size > 0 && (
            <div className="border-t border-[#C9D4E6]">
              <Swipeable hint>
                <div style={{ minWidth: numPlans >= 4 ? `${160 + numPlans * 160}px` : '600px' }}>
                  {Array.from(allRiders.entries()).map(([riderName, description], riderIdx) => (
                    <div
                      key={riderName}
                      className={`grid gap-4 p-4 ${riderIdx % 2 === 0 ? 'bg-blue-900/20' : ''}`}
                      style={{ 
                        gridTemplateColumns: numPlans >= 4
                          ? `160px repeat(${numPlans}, minmax(160px, 1fr))`
                          : `200px repeat(${numPlans}, minmax(200px, 1fr))`
                      }}
                    >
                      <div className="flex items-center">
                        <span className="text-[#2E3B57] text-sm font-medium">{riderName}</span>
                      </div>
                      {plans.map((plan) => {
                        const hasRider = plan.riders?.some(r => r.name === riderName);
                        const isSelected = plan.quotePlan?.selected_riders?.includes(riderName);
                        
                        return (
                          <div key={plan.id} className="flex items-center justify-center text-center px-2">
                            {hasRider ? (
                              <div className="text-center">
                                <span className="flex items-center justify-center gap-1 text-emerald-600">
                                  <CheckCircle2 className="h-4 w-4" />
                                  <span className="text-sm">Available</span>
                                </span>
                                {isSelected && (
                                  <span className="text-xs text-[#A87608] block mt-1">✓ Selected</span>
                                )}
                              </div>
                            ) : (
                              <span className="flex items-center justify-center gap-1 text-red-600">
                                <X className="h-4 w-4" />
                                <span className="text-sm">Not Available</span>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </Swipeable>
            </div>
          )}
        </div>

        {/* Premium Details Section */}
        <div className="glass-card overflow-hidden border-[#D4A017]/30">
          <div className="p-4 md:p-5 border-b border-[#C9D4E6] flex items-center gap-3">
            <div className="h-8 w-8 md:h-10 md:w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <IndianRupee className="h-4 w-4 md:h-5 md:w-5 text-[#1B2A4A]" />
            </div>
            <div>
              <h2 className="font-bold text-[#1B2A4A] text-sm md:text-base">Premium Details</h2>
              <p className="text-xs text-[#3A4557]">Indicative premiums - final amount may vary based on underwriting</p>
            </div>
          </div>

          <Swipeable hint>
            <table className="w-full">
              <thead>
                <tr className="bg-blue-900/30">
                  <th className="p-3 text-left text-[#2E3B57] font-medium text-sm border-r border-[#C9D4E6]">Sum Insured</th>
                  <th className="p-3 text-center text-[#2E3B57] font-medium text-sm border-r border-[#C9D4E6]">Term</th>
                  {plans.map((plan) => (
                    <th key={plan.id} className="p-3 text-center text-[#A87608] font-medium text-sm border-r border-[#C9D4E6] last:border-r-0 min-w-[140px]">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const allSIs = [...new Set(selectedPlans.flatMap(p => p.selected_si_options || []))].sort((a, b) => {
                    const parseValue = (v) => {
                      if (v.includes("Cr")) return parseFloat(v) * 100;
                      return parseFloat(v);
                    };
                    return parseValue(a) - parseValue(b);
                  });
                  
                  return allSIs.flatMap((si, siIdx) => 
                    PREMIUM_TERMS.map((term, termIdx) => {
                      const hasPremium = selectedPlans.some(p => p.premiums?.[si]?.[term.id]);
                      if (!hasPremium && termIdx > 0) return null;
                      
                      return (
                        <tr key={`${si}-${term.id}`} className={siIdx % 2 === 0 ? "bg-blue-900/20" : ""}>
                          {termIdx === 0 && (
                            <td 
                              rowSpan={PREMIUM_TERMS.filter((t, i) => i === 0 || selectedPlans.some(p => p.premiums?.[si]?.[t.id])).length}
                              className="p-3 text-[#1B2A4A] font-medium border-r border-[#C9D4E6] align-top"
                            >
                              ₹{si}
                            </td>
                          )}
                          <td className="p-3 text-[#3A4557] text-sm text-center border-r border-[#C9D4E6]">
                            {term.label}
                          </td>
                          {selectedPlans.map((quotePlan, planIdx) => {
                            const premium = quotePlan.premiums?.[si]?.[term.id];
                            const hasSI = (quotePlan.selected_si_options || []).includes(si);
                            
                            return (
                              <td key={planIdx} className="p-3 text-center border-r border-[#C9D4E6] last:border-r-0">
                                {hasSI ? (
                                  premium ? (
                                    <span className="text-emerald-600 font-semibold">₹{premium}</span>
                                  ) : (
                                    <span className="text-[#3A4557] text-sm">TBD</span>
                                  )
                                ) : (
                                  <span className="text-[#3A4557]">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    }).filter(Boolean)
                  );
                })()}
              </tbody>
            </table>
          </Swipeable>
        </div>

        {/* Know Your Rights Section - Comprehensive */}
        <div className="glass-card overflow-hidden mt-6 border-blue-500/30">
          <div className="p-4 md:p-5 border-b border-[#C9D4E6] flex items-center gap-3">
            <div className="h-8 w-8 md:h-10 md:w-10 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-cyan-500 flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 text-[#1B2A4A]" />
            </div>
            <div>
              <h2 className="font-bold text-[#1B2A4A] text-sm md:text-base">Know Your Rights as a Policyholder</h2>
              <p className="text-xs text-[#3A4557]">Important rights protected by IRDAI regulations</p>
            </div>
          </div>

          <div className="p-4 md:p-5">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-[#3A4557] text-xs">Moratorium Period</p>
                    <p className="text-green-600 font-bold">5 Years</p>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-[#EDF1F8]/30 border border-blue-500/30">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-[#2E3B57]" />
                  <div>
                    <p className="text-[#3A4557] text-xs">Claim TAT (Cashless)</p>
                    <p className="text-[#2E3B57] font-bold">1 Hour</p>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-[#D4A017]/10 border border-[#D4A017]/30">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[#A87608]" />
                  <div>
                    <p className="text-[#3A4557] text-xs">Grievance Resolution</p>
                    <p className="text-[#A87608] font-bold">15 Days</p>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-[#3A4557] text-xs">Ombudsman Complaint</p>
                    <p className="text-red-600 font-bold">1 Year Limit</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 5-Year Moratorium Rule */}
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-green-600 font-bold mb-2">
                    🛡️ The 5-Year Moratorium Rule - Your Biggest Protection
                  </h3>
                  <p className="text-[#2E3B57] text-sm mb-3">
                    After <strong className="text-[#1B2A4A]">5 continuous years</strong> of policy renewal, 
                    your health insurance policy <strong className="text-green-600">CANNOT be cancelled or claim rejected</strong> on 
                    grounds of non-disclosure or misrepresentation, except in cases of proven fraud.
                  </p>
                  <div className="bg-white/50 rounded-lg p-3">
                    <p className="text-[#1B2A4A] text-sm font-semibold mb-2">What this means:</p>
                    <ul className="space-y-1 text-[#3A4557] text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Even if you forgot to disclose a pre-existing condition, claim cannot be denied after 5 years</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Policy renewal cannot be refused citing non-disclosure</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>As per IRDAI (Health Insurance) Regulations, updated April 2024</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* IRDAI Mandated Timelines */}
            <div className="bg-[#EDF1F8]/30 border border-blue-500/30 rounded-xl p-4 mb-6">
              <h4 className="text-[#2E3B57] font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                IRDAI Mandated Timelines for Cashless Claims
              </h4>
              <ul className="space-y-2 text-[#2E3B57] text-sm">
                <li>• <strong>Pre-authorization (Emergency):</strong> Must be approved/rejected within <strong className="text-[#1B2A4A]">1 hour</strong></li>
                <li>• <strong>Planned hospitalization:</strong> Decision within <strong className="text-[#1B2A4A]">24 hours</strong></li>
                <li>• <strong>Final settlement after discharge:</strong> Within <strong className="text-[#1B2A4A]">3 hours</strong></li>
                <li>• <strong>Reimbursement claim settlement:</strong> Within <strong className="text-[#1B2A4A]">30 days</strong> of receiving all documents</li>
              </ul>
            </div>

            {/* How to Fight Rejected Claim */}
            <div className="mb-6">
              <h4 className="text-[#1B2A4A] font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-[#A87608]" />
                If Your Claim is Rejected - Escalation Process
              </h4>
              
              <div className="space-y-3">
                <div className="bg-[#EDF1F8]/50 rounded-lg p-3 border-l-4 border-blue-500">
                  <h5 className="text-[#2E3B57] font-semibold text-sm">Level 1: Insurance Company Grievance Cell</h5>
                  <p className="text-[#3A4557] text-xs mt-1">Timeline: 15 days for resolution. Write to Grievance Redressal Officer (GRO)</p>
                </div>
                
                <div className="bg-[#EDF1F8]/50 rounded-lg p-3 border-l-4 border-[#D4A017]">
                  <h5 className="text-[#A87608] font-semibold text-sm">Level 2: IGMS Portal (IRDAI)</h5>
                  <p className="text-[#3A4557] text-xs mt-1">If no response in 15 days. Visit: igms.irda.gov.in</p>
                </div>
                
                <div className="bg-[#EDF1F8]/50 rounded-lg p-3 border-l-4 border-purple-500">
                  <h5 className="text-purple-600 font-semibold text-sm">Level 3: Insurance Ombudsman</h5>
                  <p className="text-[#3A4557] text-xs mt-1">Claims up to ₹50 Lakhs. FREE, no lawyer needed. File within 1 year of rejection</p>
                </div>
                
                <div className="bg-[#EDF1F8]/50 rounded-lg p-3 border-l-4 border-red-500">
                  <h5 className="text-red-600 font-semibold text-sm">Level 4: Consumer Forum / Court</h5>
                  <p className="text-[#3A4557] text-xs mt-1">District Forum (up to ₹1 Cr), State Commission (₹1-10 Cr), National Commission (above ₹10 Cr)</p>
                </div>
              </div>
            </div>

            {/* Key Rights Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-blue-900/20 rounded-lg border border-[#C9D4E6]">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <h4 className="font-semibold text-[#1B2A4A] text-sm">Free Look Period</h4>
                </div>
                <p className="text-xs text-[#3A4557]">15-30 days to review and return policy for full refund</p>
              </div>
              
              <div className="p-3 bg-blue-900/20 rounded-lg border border-[#C9D4E6]">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <h4 className="font-semibold text-[#1B2A4A] text-sm">Lifelong Renewal</h4>
                </div>
                <p className="text-xs text-[#3A4557]">Insurer cannot refuse renewal except for fraud or non-payment</p>
              </div>
              
              <div className="p-3 bg-blue-900/20 rounded-lg border border-[#C9D4E6]">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <h4 className="font-semibold text-[#1B2A4A] text-sm">Portability Right</h4>
                </div>
                <p className="text-xs text-[#3A4557]">Switch insurers while retaining waiting period credits</p>
              </div>
              
              <div className="p-3 bg-blue-900/20 rounded-lg border border-[#C9D4E6]">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <h4 className="font-semibold text-[#1B2A4A] text-sm">Complete Policy Document</h4>
                </div>
                <p className="text-xs text-[#3A4557]">Right to receive policy document within 15 days of acceptance</p>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="p-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-lg">
              <p className="text-xs text-[#8F6A06] text-center">
                <strong>💡 IRDAI Helpline:</strong> Toll Free <span className="text-[#1B2A4A] font-bold">155255</span> | 
                Email: complaints@irdai.gov.in | 
                IGMS Portal: <span className="text-[#2E3B57]">igms.irda.gov.in</span> | 
                Ombudsman: <span className="text-[#2E3B57]">cioins.co.in</span>
              </p>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="glass-card p-6 mt-6 border-[#D4A017]/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-[#1B2A4A] mb-1">Ready to Buy?</h3>
              <p className="text-[#3A4557]">Get personalized assistance from your insurance advisor</p>
            </div>
            <div className="flex items-center gap-3">
              <a href="tel:+919510020907">
                <Button className="bg-green-600 hover:bg-green-700 px-6 py-3 h-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call: +91 9510020907
                </Button>
              </a>
              <a href="https://wa.me/919979860107" target="_blank" rel="noopener noreferrer">
                <Button className="bg-emerald-600 hover:bg-emerald-700 px-6 py-3 h-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Footer Disclaimer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#3A4557] max-w-2xl mx-auto">
            <AlertTriangle className="h-3 w-3 inline mr-1" />
            Premiums shown are indicative. Final premium depends on health conditions and underwriting. 
            IRDAI data sourced from Insurance Statistics Handbook 2024-25.
            Policy issuance subject to acceptance by insurance company.
          </p>
          <p className="text-xs text-[#3A4557] mt-2">
            © {new Date().getFullYear()} Kavach Box by Prashant K Uunadkat | A Beshak Verified Insurance Advisor
          </p>
        </div>
      </main>
    </div>
  );
}
